import React from 'react';
import { Link } from 'react-router-dom';
import { Github, Linkedin, Mail, ExternalLink, Code2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { personalInfo, projects } from '../mock';


const Home = () => {
  const featuredProjects = projects.filter(p => p.featured);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black overflow-x-hidden">
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/80 backdrop-blur-md border-b border-neutral-800 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-lg sm:text-xl font-bold text-white hover:text-gray-300 transition-colors">
            {personalInfo.name.split(' ').map((word, i) => i === 0 ? word[0] : '').join('')}VT
          </Link>
          <div className="flex items-center gap-3 sm:gap-6">
            <Link to="/projects" className="text-sm sm:text-base text-neutral-300 hover:text-white transition-colors">
              Projects
            </Link>
            <Link to="/about" className="text-sm sm:text-base text-neutral-300 hover:text-white transition-colors">
              About
            </Link>
            <Link to="/contact">
              <Button
                variant="outline"
                className="border-neutral-600 text-neutral-300 hover:bg-neutral-800 hover:text-white"
              >
                Contact Me
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 min-h-[500px] sm:min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={personalInfo.profileImage}
            alt={personalInfo.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-black/80"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-4">
              {personalInfo.name}
            </h1>
            <p className="text-lg sm:text-2xl md:text-3xl text-neutral-300 mb-8 font-light">
              {personalInfo.title}
            </p>

            <div className="flex gap-4 mb-8">
              <a href={personalInfo.socialLinks.github} target="_blank" rel="noopener noreferrer"
                className="p-3 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-transform hover:scale-110">
                <Github className="w-6 h-6" />
              </a>
              <a href={personalInfo.socialLinks.linkedin} target="_blank" rel="noopener noreferrer"
                className="p-3 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-transform hover:scale-110">
                <Linkedin className="w-6 h-6" />
              </a>
              <a href={personalInfo.socialLinks.itch} target="_blank" rel="noopener noreferrer"
                className="p-3 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-transform hover:scale-110">
                <Code2 className="w-6 h-6" />
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-gray-200 hover:bg-white text-black px-8 font-semibold">
                  <Mail className="w-4 h-4 mr-2" />
                  Get In Touch
                </Button>
              </Link>
              <a href={personalInfo.resumeUrl} download>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
                >
                  Download Resume
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-neutral-950">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-12 text-center">
            Highlighted <span className="text-neutral-400">Projects</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {featuredProjects.map((project) => (
              <Link key={project.id} to={`/project/${project.id}`}>
                <Card className="bg-black border-neutral-800 overflow-hidden group hover:border-neutral-600 transition-all hover:shadow-2xl hover:shadow-black/60">
                  
                  <div className="relative overflow-hidden">
                    <img
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-48 sm:h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-70"></div>
                  </div>

                  <CardHeader className="p-6">
                    <CardTitle className="text-white text-xl sm:text-2xl">
                      {project.title}
                    </CardTitle>
                    <CardDescription className="text-neutral-400">
                      {project.shortDescription}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-6 pt-0">
                    <div className="space-y-3 mb-4 text-sm">
                      <div>
                        <span className="text-neutral-400 font-semibold">Programming Language: </span>
                        <span className="text-neutral-300">{project.language}</span>
                      </div>
                      <div>
                        <span className="text-neutral-400 font-semibold">Type: </span>
                        <span className="text-neutral-300">{project.type}</span>
                      </div>
                    </div>

                    <Button className="w-full bg-neutral-200 hover:bg-white text-black font-semibold pointer-events-none">
                      Read More
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>

                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Me */}
      <section className="py-20 px-4 bg-neutral-900/50 text-center">
        <h2 className="text-4xl font-bold text-white mb-6">About Me</h2>
        <p className="text-neutral-300 mb-8 max-w-3xl mx-auto">
          If you wish to find out more about me and my skillset, feel free to visit my about page!
        </p>
        <Link to="/about">
          <Button size="lg" className="bg-gray-200 hover:bg-white text-black px-12 font-semibold">
            Go to About Me
          </Button>
        </Link>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-neutral-800 text-center text-neutral-400">
        &copy; 2025 {personalInfo.name}. All rights reserved.
      </footer>

    </div>
  );
};

export default Home;
